import Dexie, { Table } from 'dexie';
import { ENTITIES, OFFLINE_ENABLED } from './entities';

export type Row = { id: string; clinicId?: string; horseId?: string; [k: string]: any };
export type MetaRow = { key: string; value: any };

export type OpStatus = 'pending' | 'conflict' | 'error';

/** A queued offline mutation, in the POST /sync/push op shape + client status. */
export type OpRow = {
  id: string; // client-generated uuid (also the temp entityId for creates)
  entityType: string;
  operationType: 'create' | 'update' | 'delete';
  entityId: string; // temp id for creates; real server id for update/delete
  payload: any;
  clientTimestamp: string;
  status: OpStatus;
  table: string; // replica table this op's optimistic row lives in
  message?: string; // server message on conflict/error
  serverRecord?: any; // server's version, on conflict (for the needs-review UI)
  retries?: number; // server-rejected ('error') push attempts, for bounded retry
  /**
   * The server `updatedAt` this edit was made against (base version for
   * optimistic-concurrency conflict detection). Server-origin, so the conflict
   * check is immune to device clock skew — unlike clientTimestamp. Absent on
   * creates (nothing to conflict against) and on legacy/iOS ops (server falls
   * back to the clientTimestamp comparison). See executeUpdate / handleUpdate.
   */
  baseUpdatedAt?: string;
};

/**
 * Append-only Dexie schema history. CRITICAL: never edit a released version's
 * `stores()`. Dexie keys persisted data by version number; silently mutating an
 * existing version (e.g. by deriving v1 from the mutable ENTITIES list, the
 * original bug) throws a VersionError that BRICKS every installed PWA until the
 * user clears site data. To add an entity: append a NEW version block (Dexie
 * carries unchanged tables forward; brand-new empty tables need no upgrade()).
 * `assertSchemaCoversEntities()` fails loudly in dev if an ENTITIES table has no
 * version block — so a coverage gap is caught before it ships, not at runtime.
 *
 * Index strings mirror the historical dynamic build exactly: 'id, clinicId' for
 * flat tables and 'id, clinicId, horseId' for by-horse (chart-read) tables, so
 * already-installed local DBs upgrade in place with no schema diff.
 */
export const SCHEMA_VERSIONS: ReadonlyArray<{
  version: number;
  stores: Record<string, string>;
}> = [
  {
    version: 1,
    stores: {
      clinic: 'id',
      meta: 'key',
      horses: 'id, clinicId',
      clients: 'id, clinicId',
      users: 'id, clinicId',
      soapTemplates: 'id, clinicId',
      inventoryItems: 'id, clinicId',
      appointments: 'id, clinicId, horseId',
      soapNotes: 'id, clinicId, horseId',
      medicalRecords: 'id, clinicId, horseId',
      medications: 'id, clinicId, horseId',
      vaccinations: 'id, clinicId, horseId',
      procedures: 'id, clinicId, horseId',
      dischargeSummaries: 'id, clinicId, horseId',
      invoices: 'id, clinicId, horseId',
      clientCommunications: 'id, clinicId, horseId',
    },
  },
  // v2 adds the offline-write op-queue (purely additive — v1 tables carry forward).
  { version: 2, stores: { opQueue: 'id, status, entityType' } },
  // v3 adds the Bucket C clinical chart tables (offline coverage expansion).
  // All horse-scoped (chart reads) → indexed by horseId.
  {
    version: 3,
    stores: {
      labResults: 'id, clinicId, horseId',
      problemLists: 'id, clinicId, horseId',
      recallReminders: 'id, clinicId, horseId',
    },
  },
  // v4 adds client-scoped notes + vet calendar blocks (clinic-scoped; readers
  // filter by client/vet in memory — both low-volume per clinic).
  {
    version: 4,
    stores: {
      clientNotes: 'id, clinicId',
      timeBlocks: 'id, clinicId',
    },
  },
  // v5 adds offline billing: payments (indexed by invoiceId for balance reads)
  // + the service catalog (clinic + global pricing rows).
  {
    version: 5,
    stores: {
      payments: 'id, clinicId, invoiceId',
      serviceCatalog: 'id, clinicId',
    },
  },
  // v6 adds StockLocation (trucks, Phase 3b): pull-only reference data
  // (warehouse + truck definitions), clinic-scoped like serviceCatalog.
  {
    version: 6,
    stores: {
      stockLocations: 'id, clinicId',
    },
  },
];

/** Tables every replica must declare: the synced entity tables + fixed stores. */
export function requiredTables(entities = ENTITIES): string[] {
  return [...entities.map((e) => e.table), 'clinic', 'meta', 'opQueue'];
}

/** The store string a table is declared with (latest-declaring version wins). */
export function declaredStore(
  table: string,
  versions: ReadonlyArray<{ stores: Record<string, string> }> = SCHEMA_VERSIONS,
): string | undefined {
  let s: string | undefined;
  for (const v of versions) if (v.stores[table] !== undefined) s = v.stores[table];
  return s;
}

/** Required tables with no `stores()` declaration across the frozen history. */
export function schemaCoverageGaps(
  required: string[] = requiredTables(),
  versions: ReadonlyArray<{ stores: Record<string, string> }> = SCHEMA_VERSIONS,
): string[] {
  const declared = new Set<string>();
  for (const v of versions) for (const t of Object.keys(v.stores)) declared.add(t);
  return required.filter((t) => !declared.has(t));
}

/**
 * Fail loudly when ENTITIES gains a table with no version block — the exact
 * mistake that bricks installed PWAs. Always logs; throws only when offline is
 * enabled (a flag-off build never opens the DB, so don't crash those users for
 * an offline-only developer error).
 */
function assertSchemaCoversEntities(): void {
  const gaps = schemaCoverageGaps();
  if (gaps.length === 0) return;
  const msg =
    `OfflineDB schema is missing a version block for: ${gaps.join(', ')}. ` +
    `Append a NEW version() to SCHEMA_VERSIONS (never edit an existing one) ` +
    `whenever you add a table to ENTITIES.`;
  // eslint-disable-next-line no-console
  console.error(msg);
  if (OFFLINE_ENABLED) throw new Error(msg);
}

/**
 * Local read-replica of the clinic's synced data. Tables are declared
 * explicitly (so they can be used by name in readers/tests) AND created from
 * the ENTITIES registry. Dynamic access uses `(db as any)[table]`. `clinic` is
 * a single row; `meta` holds the sync watermark + owning clinicId.
 */
class OfflineDB extends Dexie {
  horses!: Table<Row, string>;
  clients!: Table<Row, string>;
  users!: Table<Row, string>;
  soapTemplates!: Table<Row, string>;
  inventoryItems!: Table<Row, string>;
  appointments!: Table<Row, string>;
  soapNotes!: Table<Row, string>;
  medicalRecords!: Table<Row, string>;
  medications!: Table<Row, string>;
  vaccinations!: Table<Row, string>;
  procedures!: Table<Row, string>;
  dischargeSummaries!: Table<Row, string>;
  invoices!: Table<Row, string>;
  clientCommunications!: Table<Row, string>;
  labResults!: Table<Row, string>;
  problemLists!: Table<Row, string>;
  recallReminders!: Table<Row, string>;
  clientNotes!: Table<Row, string>;
  timeBlocks!: Table<Row, string>;
  payments!: Table<Row, string>;
  serviceCatalog!: Table<Row, string>;
  stockLocations!: Table<Row, string>;
  clinic!: Table<Row, string>;
  meta!: Table<MetaRow, string>;
  opQueue!: Table<OpRow, string>;

  constructor() {
    // B14 safeguard #2: force strict (fsync-before-complete) transaction
    // durability on Chromium, closing the relaxed-durability window where a
    // just-"committed" PHI write can be lost on power loss. Runtime-only — it
    // does NOT touch the schema/version/store strings (installed-PWA upgrade
    // safety is unchanged), and is ignored (durable-by-default) on other engines.
    super('stabletrack-offline', { chromeTransactionDurability: 'strict' });
    // Frozen, append-only version history (see SCHEMA_VERSIONS). Never derive a
    // released version from the mutable ENTITIES list — that is the VersionError
    // that bricks installed PWAs.
    for (const v of SCHEMA_VERSIONS) this.version(v.version).stores(v.stores);
    assertSchemaCoversEntities();
  }
}

export const db = new OfflineDB();

/**
 * All table names the replica owns — INCLUDING the op-queue. The queue is wiped
 * on logout + clinic-switch so (a) PHI never outlives the session and (b) one
 * clinic's queued writes can never be pushed under another clinic. This wipe
 * discards ALL un-synced ops (pending AND parked conflict/error ops), so logout
 * is guarded by a confirm-on-logout prompt (useAuth) that counts the whole queue
 * via unsyncedOpCount() — matching exactly what this clear destroys.
 */
export const ALL_TABLES = [...ENTITIES.map((e) => e.table), 'clinic', 'meta', 'opQueue'];

/**
 * Wipe all replicated data + the op-queue (logout / clinic switch). Keeps the
 * schema intact so the next sync can refill. PHI must never outlive the session
 * that pulled it.
 */
export async function clearReplica(): Promise<void> {
  await db.transaction(
    'rw',
    ALL_TABLES.map((t) => (db as any)[t] as Table),
    async () => {
      for (const t of ALL_TABLES) await (db as any)[t].clear();
    },
  );
}
